import type { ResolvedTeam } from '@realsite/site-config';
import { agentInitials, type RosterAgent } from './AgentRoster';
import { formatPhone } from '../phone';

/**
 * Teams on the live site: two or more agents who work together (packages/site-config teams.ts).
 *
 * One component for every place a team shows, so they cannot drift apart: the /agents roster's
 * "Our Teams" section (tenant route and Studio, through AgentRoster), and the office page's teams.
 * The team's members, photos and phones are the roster's own; the team only adds its name, photo,
 * words and page.
 *
 * Framework-agnostic (plain <a>) so it transpiles in both Next.js and Vite.
 */

export type RosterTeam = ResolvedTeam<RosterAgent>;

/** Up to this many member faces on a card without a team photo. */
const FACES = 4;

function MemberFaces({ members, primaryColor }: { members: RosterAgent[]; primaryColor: string }) {
  const shown = members.slice(0, FACES);
  const more = members.length - shown.length;
  return (
    <div className="flex items-center justify-center -space-x-3 py-8" style={{ backgroundColor: primaryColor + '10' }}>
      {shown.map((m) => (
        m.photo ? (
          <img
            key={m.key}
            src={m.photo}
            alt=""
            className="h-16 w-16 rounded-full border-2 border-white object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span
            key={m.key}
            aria-hidden="true"
            className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-white text-lg font-bold text-white"
            style={{ backgroundColor: primaryColor }}
          >
            {agentInitials(m.name)}
          </span>
        )
      ))}
      {more > 0 && (
        <span className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-white bg-white text-sm font-semibold text-gray-600">
          +{more}
        </span>
      )}
    </div>
  );
}

export function TeamCard({
  team,
  primaryColor,
  officeName,
  linkText,
}: {
  team: RosterTeam;
  primaryColor: string;
  /** The office the team works from, when known. */
  officeName?: string | null;
  /** The words on the link to the team page. */
  linkText?: string;
}) {
  const { team: t, members, href } = team;
  const lead = t.leader_key ? members.find((m) => m.key === t.leader_key) : undefined;
  return (
    <div className="tmpl-team-card auto-list-card blk-card bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col" data-testid="team-card">
      {t.photo_url ? (
        <img src={t.photo_url} alt={t.name} className="w-full h-48 object-cover" referrerPolicy="no-referrer" />
      ) : (
        <MemberFaces members={members} primaryColor={primaryColor} />
      )}
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="text-lg font-heading font-bold text-gray-900">
          {href ? <a href={href} className="hover:underline">{t.name}</a> : t.name}
        </h3>
        {officeName && <p className="text-xs text-gray-400 mt-0.5">{officeName}</p>}
        <p className="text-sm font-semibold mt-2" style={{ color: primaryColor }}>
          {members.length} agent{members.length !== 1 ? 's' : ''}
          {lead ? <span className="font-normal text-gray-500"> · Led by {lead.name}</span> : null}
        </p>
        <ul className="mt-2 text-sm text-gray-600 space-y-0.5">
          {members.map((m) => (
            <li key={m.key}>
              {m.slug ? <a href={`/agents/${m.slug}`} className="hover:underline">{m.name}</a> : m.name}
            </li>
          ))}
        </ul>
        {(t.phone || t.email || href) && (
          <div className="mt-auto pt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            {t.phone && (
              <a href={`tel:${t.phone}`} className="blk-link font-medium hover:underline" style={{ color: primaryColor }}>{formatPhone(t.phone)}</a>
            )}
            {t.email && (
              <a href={`mailto:${t.email}`} className="blk-link font-medium hover:underline" style={{ color: primaryColor }}>Email</a>
            )}
            {href && (
              <a href={href} className="blk-link inline-flex items-center gap-1 font-semibold hover:underline" style={{ color: primaryColor }}>
                {(linkText ?? '').trim() || 'Meet the team'}
                <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** A band of team cards with its heading: "Our Teams" on the roster, "Teams in this office" on an office page. */
export function TeamsSection({
  teams,
  primaryColor,
  heading = 'Our Teams',
  officeNameFor,
  className = 'auto-list-band py-16 bg-white',
}: {
  teams: RosterTeam[];
  primaryColor: string;
  heading?: string;
  /** The office name a card shows, if any. */
  officeNameFor?: (team: RosterTeam) => string | null | undefined;
  className?: string;
}) {
  if (!teams.length) return null;
  return (
    <section className={className} data-testid="teams-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-heading font-bold text-gray-900 mb-10 text-center">{heading}</h2>
        <div className="auto-list-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {teams.map((t) => (
            <TeamCard key={t.team.id} team={t} primaryColor={primaryColor} officeName={officeNameFor?.(t)} />
          ))}
        </div>
      </div>
    </section>
  );
}
